import { SelectLabel } from '@radix-ui/react-select'
import clsx from 'clsx'
import { Check, X } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'

import { IconButton } from 'slab'
import { type BrokersEnrollmentEnriched } from 'utils/web_api.schemas'

import { sanitizeNumericInput } from '../../utils'
import CoverageAuditReasonContext from '../context/CoverageAuditReasonContext'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

interface UpdateFieldOnChangeProps<T, U> {
  value: U
  field: keyof T
  label?: string | React.ReactNode
  onChange: (field: keyof T, value: U) => void
  placeholder?: string
  isDisabled?: boolean
  tooltip?: React.ReactNode
}

type UpdateEnrollmentFieldOnChangeTextProps = UpdateFieldOnChangeProps<
  BrokersEnrollmentEnriched,
  string
>
type UpdateEnrollmentFieldOnChangeBooleanProps = UpdateFieldOnChangeProps<
  BrokersEnrollmentEnriched,
  boolean
>

export type EnrollmentSelectOption = {
  value: string
  label: string | React.ReactNode
  tag?: React.ReactNode
}

export type EnrollmentSelectOptionGroups = Record<
  string,
  { label: string; options: EnrollmentSelectOption[] }
>

// UpdateFieldOnChangeFormText is a component that allows the user to update a field in a form
// by typing in a new value.
export const UpdateFieldOnChangeFormText = ({
  value,
  field,
  label,
  isMoneyAmount,
  onChange,
  placeholder,
  tooltip,
}: UpdateEnrollmentFieldOnChangeTextProps & {
  isMoneyAmount?: boolean
}) => {
  const [updatedValue, setUpdatedValue] = useState<string>(value)

  useEffect(() => setUpdatedValue(value), [value])

  const isDisabled = updatedValue === value

  const onClickHandler = () => {
    onChange(field, updatedValue)
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    // If its a form field for money amount, only allow numbers and decimal points.
    setUpdatedValue(isMoneyAmount ? sanitizeNumericInput(value) : value)
  }

  return (
    <div className='flex justify-between items-center mb-1 space-x-1'>
      <label className='w-1/3 flex items-center space-x-1'>
        <span className='whitespace-nowrap text-ellipsis overflow-hidden'>
          {label}
        </span>
        <span className='flex item-center'>{tooltip}</span>
      </label>
      <div className='w-2/3 flex items-center space-x-1 justify-end'>
        <input
          className={clsx({
            'input-default': true,
            'w-[180px]': isMoneyAmount,
          })}
          value={updatedValue as string}
          onChange={onChangeHandler}
          placeholder={placeholder}
        />
        <IconButton
          disabled={isDisabled}
          onClick={onClickHandler}
          data-testid={isDisabled ? undefined : 'update_field_on_change_button'}
          icon={Check}
          label='Confirm changes'
          variant='primary'
          size='large'
        />
        <IconButton
          disabled={isDisabled}
          onClick={() => setUpdatedValue(value)}
          icon={X}
          label='Cancel changes'
          variant='secondary'
          size='large'
        />
      </div>
    </div>
  )
}

// UpdateFieldOnChangeFormBoolean is a component that allows the user to update a field in a form by
// toggling a boolean value.
export const UpdateFieldOnChangeFormBoolean = ({
  value,
  field,
  label,
  onChange,
  requireReason = false,
}: UpdateEnrollmentFieldOnChangeBooleanProps & {
  requireReason?: boolean
}) => {
  const [updatedValue, setUpdatedValue] = useState<boolean>(value)
  const { reason, openReasonDropdown } = useContext(CoverageAuditReasonContext)
  const isDisabled = updatedValue === value
  const hasNoReason = !reason

  const onClickHandler = () => {
    if (requireReason && hasNoReason) {
      openReasonDropdown?.()
      return
    }
    if (!isDisabled) {
      onChange(field, updatedValue)
    }
  }

  useEffect(() => setUpdatedValue(value), [value])

  return (
    <div className='flex justify-between items-center mb-1'>
      <label className='w-1/3 whitespace-nowrap text-ellipsis overflow-hidden'>
        {label}
      </label>
      <div className='w-2/3 flex justify-end items-center space-x-1'>
        <Select
          onValueChange={(value) => setUpdatedValue(value === 'true')}
          value={updatedValue ? 'true' : 'false'}
        >
          <SelectTrigger className='w-[100px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ].map(({ value, label }, index) => (
              <SelectItem key={index} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <IconButton
          disabled={isDisabled}
          onClick={onClickHandler}
          icon={Check}
          label='Confirm changes'
          variant='primary'
          size='large'
        />
        <IconButton
          disabled={isDisabled}
          onClick={() => setUpdatedValue(value)}
          icon={X}
          label='Cancel changes'
          variant='secondary'
          size='large'
        />
      </div>
    </div>
  )
}

// UpdateFieldOnChangeFormSelect is a component that allows the user to update a field in a form by selecting
// a value from a list of options.
export const UpdateFieldOnChangeFormSelect = <T,>({
  value,
  field,
  label,
  options,
  onChange,
  placeholder,
  resetValueOnChange,
  isDisabled = false,
  tooltip,
  requireReason = false,
}: UpdateFieldOnChangeProps<T, string> & {
  options: EnrollmentSelectOption[]
  resetValueOnChange?: boolean
  requireReason?: boolean
}) => {
  const [updatedValue, setUpdatedValue] = useState<string>(value)
  const { reason, openReasonDropdown } = useContext(CoverageAuditReasonContext)

  const isUpdateDisabled = isDisabled || updatedValue === value
  const hasNoReason = !reason
  const isLabeled = !!label

  const onClickHandler = () => {
    if (requireReason && hasNoReason) {
      openReasonDropdown?.()
      return
    }
    if (!isUpdateDisabled) {
      onChange(field, updatedValue)
      if (resetValueOnChange) setUpdatedValue('')
    }
  }

  useEffect(() => setUpdatedValue(value), [value])

  return (
    <div className='flex justify-between items-center mb-1'>
      {isLabeled && (
        <label className='w-1/2 flex items-center space-x-1'>
          <span className='whitespace-nowrap text-ellipsis overflow-hidden'>
            {label}
          </span>
          <span className='flex item-center'>{tooltip}</span>
        </label>
      )}
      <div
        className={clsx({
          'flex justify-end items-center space-x-1': true,
          'w-full': !isLabeled,
          'w-1/2': isLabeled,
        })}
      >
        <Select
          onValueChange={(value) => setUpdatedValue(value)}
          value={updatedValue}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map(({ value, label, tag }, index) => (
              <SelectItem key={index} value={value}>
                <div className='flex items-center'>
                  <div>{label}</div>
                  {tag && (
                    <div className='ml-1 align-middle cursor-pointer'>
                      {tag}
                    </div>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <IconButton
          disabled={isUpdateDisabled}
          onClick={onClickHandler}
          icon={Check}
          label='Confirm changes'
          variant='primary'
          size='large'
        />
        <IconButton
          disabled={isUpdateDisabled}
          onClick={() => setUpdatedValue(value)}
          icon={X}
          label='Cancel changes'
          variant='secondary'
          size='large'
        />
      </div>
    </div>
  )
}

// UpdateFieldOnChangeFormSelectGroups is a component that allows the user to update a field in a form by
// selecting a value from a list of options grouped by category.
export const UpdateFieldOnChangeFormSelectGroups = <T,>({
  value,
  field,
  label,
  groups,
  onChange,
  placeholder,
  resetValueOnChange,
  isDisabled = false,
  tooltip,
  requireReason = false,
}: UpdateFieldOnChangeProps<T, string> & {
  groups: EnrollmentSelectOptionGroups
  resetValueOnChange?: boolean
  requireReason?: boolean
}) => {
  const [updatedValue, setUpdatedValue] = useState<string>(value)
  const { reason, openReasonDropdown } = useContext(CoverageAuditReasonContext)

  const isUpdateDisabled = isDisabled || updatedValue === value
  const hasNoReason = !reason
  const isLabeled = !!label

  const onClickHandler = () => {
    if (requireReason && hasNoReason) {
      openReasonDropdown?.()
      return
    }
    if (!isUpdateDisabled) {
      onChange(field, updatedValue)
      if (resetValueOnChange) setUpdatedValue('')
    }
  }

  useEffect(() => setUpdatedValue(value), [value])

  return (
    <div className='flex justify-between items-center mb-1'>
      {isLabeled && (
        <label className='w-1/3 flex items-center space-x-1'>
          <span className='whitespace-nowrap text-ellipsis overflow-hidden'>
            {label}
          </span>
          <span className='flex item-center'>{tooltip}</span>
        </label>
      )}
      <div
        className={clsx({
          'flex justify-end items-center space-x-1': true,
          'w-full': !isLabeled,
          'w-2/3': isLabeled,
        })}
      >
        <Select
          onValueChange={(value) => setUpdatedValue(value)}
          value={updatedValue}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {Object.values(groups).map(({ label, options }, index) => (
              <SelectGroup key={index}>
                <SelectLabel className='font-semibold ml-1'>
                  {label}
                </SelectLabel>
                {options.map(({ value, label }, index) => (
                  <SelectItem key={index} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <IconButton
          disabled={isUpdateDisabled}
          onClick={onClickHandler}
          icon={Check}
          label='Confirm changes'
          variant='primary'
          size='large'
        />
        <IconButton
          disabled={isUpdateDisabled}
          onClick={() => setUpdatedValue(value)}
          icon={X}
          label='Cancel changes'
          variant='secondary'
          size='large'
        />
      </div>
    </div>
  )
}
